import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import POSDashboard from "@/pages/POSDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminBranches from "@/pages/AdminBranches";
import AdminServices from "@/pages/AdminServices";
import AdminProducts from "@/pages/AdminProducts";
import AdminInventory from "@/pages/AdminInventory";
import AdminSupplies from "@/pages/AdminSupplies";
import AdminSupplyInventory from "@/pages/AdminSupplyInventory";
import AdminUsers from "@/pages/AdminUsers";
import AdminBarbers from "@/pages/AdminBarbers";
import AdminBarberSales from "@/pages/AdminBarberSales";
import AdminReports from "@/pages/AdminReports";
import AdminBalance from "@/pages/AdminBalance";
import AdminDailyCut from "@/pages/AdminDailyCut";
import AdminDailyCutServices from "@/pages/AdminDailyCutServices";
import AdminDailyCutProducts from "@/pages/AdminDailyCutProducts";
import AdminAppointments from "@/pages/AdminAppointments";
import AdminLoyalty from "@/pages/AdminLoyalty";
import AdminFixedExpenses from "@/pages/AdminFixedExpenses";
import AdminCashRegister from "@/pages/AdminCashRegister";
import AdminPartners from "@/pages/AdminPartners";
import AdminZones from "@/pages/AdminZones";
import AdminZonesRanking from "@/pages/AdminZonesRanking";
import AdminRestockRequests from "@/pages/AdminRestockRequests";
import ZoneManagerDashboard from "@/pages/ZoneManagerDashboard";
import BookingLanding from "@/pages/BookingLanding";
import BarberDashboard from "@/pages/BarberDashboard";
import ReceptionistDashboard from "@/pages/ReceptionistDashboard";
import UserDashboard from "@/pages/UserDashboard";
import LoyaltyRegister from "@/pages/LoyaltyRegister";
import LoyaltyLogin from "@/pages/LoyaltyLogin";
import { AuthProvider, useAuth } from "@/context/AuthContext";

const ProtectedRoute = ({ children, requiredRole, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-[#050505]">
        <div className="text-gold text-lg">Cargando...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Check for allowed roles array (e.g., ['master_admin', 'admin'])
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (user.role === 'zone_manager') {
      return <Navigate to="/gerente" replace />;
    } else if (user.role === 'master_admin' || user.role === 'admin' || user.role === 'manager') {
      return <Navigate to="/admin" replace />;
    } else if (user.role === 'recepcionista') {
      return <Navigate to="/pos" replace />;
    } else if (user.role === 'barber') {
      return <Navigate to="/barber" replace />;
    }
    return <Navigate to="/login" replace />;
  }
  
  // Legacy support for requiredRole
  if (requiredRole === 'admin' && !['admin', 'master_admin', 'manager', 'recepcionista'].includes(user.role)) {
    if (user.role === 'zone_manager') {
      return <Navigate to="/gerente" replace />;
    } else if (user.role === 'barber') {
      return <Navigate to="/barber" replace />;
    }
    return <Navigate to="/pos" replace />;
  }
  
  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();
  const location = useLocation();

  // Facebook Pixel: PageView on every route change (base pixel in index.html fires once on initial load)
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
    }
  }, [location.pathname]);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/citas" element={<BookingLanding />} />
      <Route
        path="/pos"
        element={
          <ProtectedRoute>
            <POSDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/branches"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminBranches />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/services"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminServices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/products"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminProducts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/inventory"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminInventory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/supplies"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminSupplies />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/supplies/inventory"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminSupplyInventory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminUsers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/barbers"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminBarbers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/barbers/sales"
        element={
          <ProtectedRoute allowedRoles={['master_admin']}>
            <AdminBarberSales />
          </ProtectedRoute>
        }
      />
      <Route
        path="/barber"
        element={
          <ProtectedRoute>
            <BarberDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recepcion"
        element={
          <ProtectedRoute allowedRoles={['recepcionista']}>
            <ReceptionistDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reports"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reports/balance"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminBalance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/daily-cut"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminDailyCut />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/daily-cut-services"
        element={
          <ProtectedRoute allowedRoles={['master_admin']}>
            <AdminDailyCutServices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/daily-cut-products"
        element={
          <ProtectedRoute allowedRoles={['master_admin']}>
            <AdminDailyCutProducts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/appointments"
        element={
          <ProtectedRoute>
            <AdminAppointments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/loyalty"
        element={
          <ProtectedRoute allowedRoles={['master_admin', 'recepcionista']}>
            <AdminLoyalty />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/fixed-expenses"
        element={
          <ProtectedRoute allowedRoles={['master_admin']}>
            <AdminFixedExpenses />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/cash-register"
        element={
          <ProtectedRoute allowedRoles={['master_admin', 'admin', 'recepcionista']}>
            <AdminCashRegister />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/partners"
        element={
          <ProtectedRoute allowedRoles={['master_admin']}>
            <AdminPartners />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/zones"
        element={
          <ProtectedRoute allowedRoles={['master_admin']}>
            <AdminZones />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/zones-ranking"
        element={
          <ProtectedRoute allowedRoles={['master_admin']}>
            <AdminZonesRanking />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/restock-requests"
        element={
          <ProtectedRoute allowedRoles={['master_admin']}>
            <AdminRestockRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/gerente"
        element={
          <ProtectedRoute allowedRoles={['zone_manager']}>
            <ZoneManagerDashboard />
          </ProtectedRoute>
        }
      />
      {/* Loyalty User Routes (public) */}
      <Route path="/lealtad" element={<UserDashboard />} />
      <Route path="/lealtad/registro" element={<LoyaltyRegister />} />
      <Route path="/lealtad/login" element={<LoyaltyLogin />} />
      <Route
        path="/"
        element={
          user ? (
            <Navigate to={
              user.role === 'zone_manager' ? '/gerente' :
              user.role === 'recepcionista' ? '/pos' :
              ['admin', 'master_admin', 'manager'].includes(user.role) ? '/admin' : 
              user.role === 'barber' ? '/barber' : '/pos'
            } replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
